<template>
  <PageContainer>
    <div class="flex flex-row place-content-around justify-between align-center gap-2">
      <div class="flex flex-col place-content-top text-left pt-4">
        <div class="">
          <span class="font-medium text-3xl mr-2">{{ formatScore(averageConsistency) }}</span>
          <span>consistency score</span>
        </div>
        <!-- TODO: Get score bands from backend endpoint. -->
        <div v-if="averageConsistency >= 90" class="text-gray-800">Your sleep consistency is <span class="font-medium">optimal</span>.</div>
        <div v-else-if="averageConsistency >= 80" class="text-gray-800">Your sleep consistency is <span class="font-medium">good</span>.</div>
        <div v-else class="text-gray-800">Your sleep consistency is <span class="font-medium">poor</span>.</div>
      </div>
      <MetricSleepConsistency
        class="cursor-pointer"
        @click="infoConsistency = true"
        :dateFrom='dateFrom'
        :dateTo='dateTo'
        :hideLabel='true'
        :hideName='true'
      />
    </div>
    <div class="relative w-full h-72 mt-2">
      <div v-if="loading" class="absolute flex flex-col items-center justify-center top-0 -bottom-12 w-full bg-white z-10">
        <object class="h-8 w-auto pointer-events-none animate-ping" :data="logoImage" type="image/svg+xml"></object>
        <p class="mt-4 text-gray-400">Loading...</p>
      </div>
      <TimeseriesGraph
        v-if='graphConsistency'
        :dataset='dataConsistency'
        :stepSize='20'
        :formatter='formatScore'
        :targetLevels='averageConsistency >= 80 ? [90] : [80]'
        @on-hover='showTooltip'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-if='graphSchedule'
        :dataset='dataSchedule'
        :stepSize='2*60'
        :formatter='formatTime'
        :targetRanges='[[targetWake, targetWake - 30], [targetBed, targetBed + 30]]'
        @on-hover='showTooltip'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if='graphBedTime'
        :dataset='dataBedTime'
        :stepSize='60'
        :formatter='formatTime'
        :targetRanges='[[targetBed, targetBed + 30]]'
        :line='true'
        @on-hover='showTooltip'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if='graphWakeTime'
        :dataset='dataWakeTime'
        :stepSize='60'
        :formatter='formatTime'
        :targetRanges='[[targetWake, targetWake - 30]]'
        :line='true'
        @on-hover='showTooltip'
        :dateFormat='dateFormat'
      />
    </div>
    <!-- TODO: On-hover for the buttons. -->
    <!-- TODO: Yearly graph view. -->
    <div class="grid grid-cols-3 gap-2 w-full bg-gray-50 rounded-lg mt-2">
      <div @click="setRangeWeekly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeWeekly }">7d</div>
      <div @click="setRangeMonthly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeMonthly }">4w</div>
      <div @click="setRangeHalfly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeHalfly }">6m</div>
      <!-- <div @click="setRangeYearly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeYearly }">1y</div> -->
    </div>
    <div class="-mx-4 overflow-x-auto">
      <div class="flex flex-row gap-2 w-full mt-3 mb-8 mx-4 w-max">
        <div @click="setGraphConsistency()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphConsistency }">Consistency</div>
        <div @click="setGraphSchedule()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphSchedule }">Schedule</div>
        <div @click="setGraphBedTime()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphBedTime }">Bed time</div>
        <div @click="setGraphWakeTime()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphWakeTime }">Wake time</div>
      </div>
    </div>
    <div class="mt-4 text-left">
      <h2>Bed time</h2>
      <div class="grid grid-cols-2 gap-4 justify-between">
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatTime(averageBedTime) }}</div>
          <div>Average bed time</div>
        </div>
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatDuration(rangeBedTime) }}</div>
          <div>Bed time range</div>
        </div>
      </div>
      <div v-if="dataLength >= 12" class="w-full h-36 mt-4">
        <DistributionGraph
          :dataset="dataBedTimeDistribution"
        />
      </div>
    </div>
    <div class="mt-4 text-left">
      <h2>Wake time</h2>
      <div class="grid grid-cols-2 gap-4 justify-between">
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatTime(averageWakeTime) }}</div>
          <div>Average wake time</div>
        </div>
        <div class="flex flex-col border-2 border-teal-100 rounded-lg p-2">
          <div class="text-xl font-medium min-h-[28px]">{{ formatDuration(rangeWakeTime) }}</div>
          <div>Wake time range</div>
        </div>
      </div>
      <div v-if="dataLength >= 12" class="w-full h-36 mt-4">
        <DistributionGraph
          :dataset="dataWakeTimeDistribution"
        />
      </div>
    </div>
  </PageContainer>

  <!-- MODALS -->
  <InformationConsistency
    v-if="infoConsistency"
    v-model="infoConsistency"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue'
import PageContainer from '@/components/PageContainer'
import MetricSleepConsistency from '@/components/MetricSleepConsistency'
import TimeseriesGraph from '@/components/TimeseriesGraph'
import DistributionGraph from '@/components/DistributionGraph'
import { minutesToTime, formatDuration, isSameDate } from '@/helpers/time'
import moment from 'moment'
import { mapStores } from 'pinia'
import { useUserStore } from '@/stores/user'

const GRAPH_CONSISTENCY = 0
const GRAPH_SCHEDULE = 1
const GRAPH_BEDTIME = 2
const GRAPH_WAKETIME = 3

const RANGE_DAYS_7 = 7 - 1
const RANGE_WEEKS_4 = 7 * 4 - 1
const RANGE_MONTHS_6 = 7 * 4 * 6 - 1
const RANGE_YEARS_1 = 367 - 1

export default {
  name: 'DataSchedule',
  components: {
    PageContainer,
    MetricSleepConsistency,
    TimeseriesGraph,
    DistributionGraph,
    InformationConsistency: defineAsyncComponent(
      () => import('@/components/modals/InformationConsistency'),
    ),
  },
  data() {
    let d = new Date()
    d.setDate(d.getDate() - RANGE_DAYS_7)

    return {
      graph: GRAPH_CONSISTENCY,
      range: RANGE_DAYS_7,
      loading: true,
      user: undefined,
      dateFrom: d.format(),
      dateTo: new Date().format(),
      dataSchedule: undefined,
      dataLength: undefined,  // Stores data length before padding.
      targetBed: undefined,
      targetWake: undefined,
      averageConsistency: undefined,
      dataBedTimeDistribution: undefined,
      dataWakeTimeDistribution: undefined,
      logoImage: require('@/assets/logo.svg'),
      infoConsistency: false,
    }
  },
  async beforeMount() {
    this.loadConsistency()
    this.loadSleeps()
    this.loadTarget()
  },
  watch: {
    // TODO: Also watch `dateTo` when implementing date range picker.
    dateFrom() {
      this.loadConsistency()
      this.loadSleeps()
    },
  },
  methods: {
    async loadConsistency() {
      const consistency = await this.userStore.user.getSleepConsistencyScore(this.dateFrom, this.dateTo)
      this.averageConsistency = consistency.score
    },
    async loadSleeps() {
      this.loading = true
      const sleeps = await this.userStore.user.getSleeps(this.dateFrom, this.dateTo, true)

      // Fill data for timeseries graphs.
      let data = []
      for (const sleep of sleeps) {
        let score = await sleep.consistency
        data.push({
          date: sleep.date,
          datapoint: [this.timestampToMinutes(sleep.date, sleep.timeEnd), this.timestampToMinutes(sleep.date, sleep.timeStart)],
          score: score,
        })
      }

      // Pad array so its length is in the range.
      this.dataLength = data.length
      data = this.padArray(data, this.range + 1)
      if (data.length > RANGE_WEEKS_4 + 1) {
        // data = this.weeklyAggregate(data)
        data = this.monthlyAggregate(data)
      }

      // TODO: Sort by date.
      this.dataSchedule = data
      // this.dataSchedule.sort()
      // this.dataSchedule = await sleeps.map(async (x) => await x.consistency)

      // Fill data for distribution graphs.
      this.dataBedTimeDistribution = this.rangeArray(Math.floor(this.minBedTime / 30) * 30 - (60 * 3), Math.floor(this.maxBedTime / 30) * 30 + (60 * 1.5), 30)
      this.dataWakeTimeDistribution = this.rangeArray(Math.floor(this.minWakeTime / 30) * 30 - (60 * 1.5), Math.floor(this.maxWakeTime / 30) * 30 + (60 * 3), 30)

      // Assign bucket by getting the floor of the time to the nearest half hour.
      this.dataBedTimeDistribution = this.dataBedTimeDistribution.map(b => {
        let result = b
        result.count = sleeps.filter(sleep => {
          let value = Math.floor(this.timestampToMinutes(sleep.date, sleep.timeStart) / 30) * 30
          return b.bucket === value
        }).length
        // Format bucket labels.
        result.bucket = this.formatTime(b.bucket)
        return result
      })
      this.dataWakeTimeDistribution = this.dataWakeTimeDistribution.map(b => {
        let result = b
        result.count = sleeps.filter(sleep => {
          let value = Math.floor(this.timestampToMinutes(sleep.date, sleep.timeEnd) / 30) * 30
          return b.bucket === value
        }).length
        // Format bucket labels.
        result.bucket = this.formatTime(b.bucket)
        return result
      })
      this.loading = false
    },
    async loadTarget() {
      // TODO: Set optimal, good, ok, poor levels.
      // TODO: Load target from plan instead of user target.
      const targets = await this.userStore.user.getSleepTarget()

      // TODO: Extract out this calculation (and from `sleep.js`).
      let bed = moment(targets.bedTime, 'HH:mm')
      let wake = moment(targets.wakeTime, 'HH:mm')

      // Set the target bedtime date to the date of the sleep.
      const sleepDate = new Date()
      const sleepMoment = moment(sleepDate)
      bed.set({
        'year': sleepMoment.year(),
        'month': sleepMoment.month(),
        'date': sleepMoment.date(),
      })
      wake.set({
        'year': sleepMoment.year(),
        'month': sleepMoment.month(),
        'date': sleepMoment.date(),
      })
      if (wake.isBefore(bed)) {
        bed.subtract(1, 'd')
      }

      this.targetBed = this.timestampToMinutes(sleepDate, bed)
      this.targetWake = this.timestampToMinutes(sleepDate, wake)
    },
    padArray(arr, len) {
      if (arr.length === len) {
        return arr
      }

      // Pad array with date but empty datapoint field.
      let d = new Date(this.dateFrom)

      // Start with an empty date array.
      let empty = Array(len).fill({})
      const fill = empty.map((element) => {
        element = {
          date: new Date(d),
          datapoint: [null, null],
          score: null,
        }
        d.setDate(d.getDate() + 1)
        return element
      })

      // Go through existing datapoints and fill where date matches.
      return fill.map((element) => {
        const match = arr?.find(e => isSameDate(e.date, element.date))
        if (match) {
          return {...element, ...match}
        } else {
          return element
        }
      })
    },
    weeklyAggregate(arr) {
      let result = []
      const reverse = arr.toReversed()
      for (let i = 0; i < reverse.length; i++) {
        let week = []
        for (let j = 0; j < 7; j++) {
          week.push(reverse.pop())
        }
        result.push({
          date: week[0].date,
          datapoint: [
            this.average(week.map(d => d.datapoint ? d.datapoint[0] : null)),
            this.average(week.map(d => d.datapoint ? d.datapoint[1] : null)),
          ],
          score: this.average(week.map(d => d.score)),
        })
      }
      return result
    },
    monthlyAggregate(arr) {
      let result = []
      const reverse = arr.toReversed()
      for (let i = 0; i < reverse.length; i++) {
        let month = []
        const currentMonth = reverse[reverse.length - 1]?.date.getMonth()
        while (currentMonth === reverse[reverse.length - 1]?.date.getMonth()) {
          month.push(reverse.pop())
        }
        result.push({
          date: month[0].date,
          datapoint: [
            this.average(month.map(d => d.datapoint ? d.datapoint[0] : null)),
            this.average(month.map(d => d.datapoint ? d.datapoint[1] : null)),
          ],
          score: this.average(month.map(d => d.score)),
        })
      }
      return result
    },
    timestampToMinutes(date, ts) {
      const zero = new Date(date)
      const diff = new Date(ts)
      zero.setHours(0, 0, 0, 0)
      // Milliseconds to minutes.
      return (diff - zero) / (1000 * 60)
    },
    rangeArray(start, stop, step) {
      return Array.from(
        { length: Math.ceil((stop - start) / step) },
        (_, i) => {
          return { bucket: start + i * step, count: 0}
        }
      )
    },
    formatScore(value) {
      return (!value && value != 0) ? '' : Math.floor(value)
    },
    formatTime(value) {
      return value === null || isNaN(value) ? '' : minutesToTime(value)
    },
    // TODO:
    // formatTimeShort() {
    // },
    formatDuration(value) {
      return value === null || isNaN(value) ? '' : formatDuration(value / 60)
    },
    showTooltip() {
    // showTooltip(context) {
    //   console.log('showTooltip():', context)
    },
    setGraphConsistency() {
      this.graph = GRAPH_CONSISTENCY
    },
    setGraphSchedule() {
      this.graph = GRAPH_SCHEDULE
    },
    setGraphBedTime() {
      this.graph = GRAPH_BEDTIME
    },
    setGraphWakeTime() {
      this.graph = GRAPH_WAKETIME
    },
    setRangeWeekly() {
      this.range = RANGE_DAYS_7
      let d = new Date()
      d.setDate(d.getDate() - RANGE_DAYS_7)
      this.dateFrom = d.format()
    },
    setRangeMonthly() {
      this.range = RANGE_WEEKS_4
      let d = new Date()
      d.setDate(d.getDate() - RANGE_WEEKS_4)
      this.dateFrom = d.format()
    },
    setRangeHalfly() {
      this.range = RANGE_MONTHS_6
      let d = new Date()
      d.setDate(d.getDate() - RANGE_MONTHS_6)
      this.dateFrom = d.format()
    },
    setRangeYearly() {
      this.range = RANGE_YEARS_1
      let d = new Date()
      d.setDate(d.getDate() - RANGE_YEARS_1)
      this.dateFrom = d.format()
    },
    average(array) {
      const nonNullLength = array?.filter(x => x != null).length
      if (array?.length === 0 || nonNullLength === 0) return null
      return array?.reduce((a, b) => a + b) / nonNullLength
    },
  },
  computed: {
    bedTimes() {
      if (this.dataSchedule?.length === 0) {
        return null
      }
      return this.dataSchedule?.map((x) => x.datapoint ? x.datapoint[1] : null).filter(x => x !== null)
    },
    wakeTimes() {
      if (this.dataSchedule?.length === 0) {
        return null
      }
      return this.dataSchedule?.map((x) => x.datapoint ? x.datapoint[0] : null).filter(x => x !== null)
    },
    averageBedTime() {
      return this.average(this.bedTimes)
    },
    averageWakeTime() {
      return this.average(this.wakeTimes)
    },
    // TODO: Remove min and max?
    maxBedTime() {
      return this.bedTimes ? Math.max(...this.bedTimes) : null
    },
    maxWakeTime() {
      return this.wakeTimes ? Math.max(...this.wakeTimes) : null
    },
    minBedTime() {
      return this.bedTimes ? Math.min(...this.bedTimes) : null
    },
    minWakeTime() {
      return this.wakeTimes ? Math.min(...this.wakeTimes) : null
    },
    rangeBedTime() {
      if (this.maxBedTime === null || this.minBedTime === null) return null
      return this.maxBedTime - this.minBedTime
    },
    rangeWakeTime() {
      if (this.maxWakeTime === null || this.minWakeTime === null) return null
      return this.maxWakeTime - this.minWakeTime
    },
    graphConsistency() {
      return this.graph === GRAPH_CONSISTENCY
    },
    graphSchedule() {
      return this.graph === GRAPH_SCHEDULE
    },
    graphBedTime() {
      return this.graph === GRAPH_BEDTIME
    },
    graphWakeTime() {
      return this.graph === GRAPH_WAKETIME
    },
    dataConsistency() {
      return this.dataSchedule?.map(d => {
        return {
          date: d.date,
          datapoint: d.score ?? null,
        }
      })
    },
    dataBedTime() {
      return this.dataSchedule?.map(d => {
        return {
          date: d.date,
          datapoint: d.datapoint ? d.datapoint[1] : null,
        }
      })
    },
    dataWakeTime() {
      return this.dataSchedule?.map(d => {
        return {
          date: d.date,
          datapoint: d.datapoint ? d.datapoint[0] : null,
        }
      })
    },
    rangeWeekly() {
      return this.range === RANGE_DAYS_7
    },
    rangeMonthly() {
      return this.range === RANGE_WEEKS_4
    },
    rangeHalfly() {
      return this.range === RANGE_MONTHS_6
    },
    rangeYearly() {
      return this.range === RANGE_YEARS_1
    },
    dateFormat() {
      switch (this.range) {
        case RANGE_DAYS_7:
          return { weekday: 'short' }
        case RANGE_WEEKS_4:
          return { month: 'short', day: 'numeric' }
        case RANGE_MONTHS_6:
          return { month: 'short' }
        case RANGE_YEARS_1:
          return { month: 'short' }
        default:
          return null
      }
    },
    ...mapStores(useUserStore),
  },
}
</script>